import MD5 from 'md5';
import { customAlphabet } from 'nanoid';
import scaleTransition, { NUMBER_LOWER_UPPER } from './scaleTransition';

const fastCustomAlphabet = (alphabet: string, defaultSize?: number) => {
  if (alphabet.length < 2) {
    throw new Error('The alphabet must contain at least two characters.');
  }

  let counter = 0; // 自增计数器

  return function generateId(size = defaultSize) {
    const timestamp = Date.now(); // 当前时间戳（毫秒）
    counter += 1; // 每次调用递增计数器
    let combined = timestamp * 1000 + counter; // 组合时间戳和计数器

    // 将数值编码为指定字母表的字符串
    const base = alphabet.length;
    let encoded = '';
    do {
      encoded = alphabet[combined % base] + encoded;
      combined = Math.floor(combined / base);
    } while (combined > 0);

    // 生成 ID 并确保长度符合要求
    let id = encoded;

    // 如果生成的 ID 长度不足，左侧填充字母表的第一个字符
    if (id.length < size) {
      id = alphabet[0].repeat(size - id.length) + id;
    }

    // 如果生成的 ID 长度超过，截取后面的部分
    if (id.length > size) {
      id = id.slice(-size);
    }

    return id;
  };
};

/**
 * 使用nanoid库, 生成数字, 小写字母, 大写字母构成的UUID
 */
const uuidNanoid = /*#__PURE__*/ customAlphabet(
  '1234567890abcdefghigklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
  32,
);
/**
 * 使用nanoid库, 生成数字加小写字母构成的UUID
 */
export const generateLowerCaseShortid = /*#__PURE__*/ customAlphabet(
  '1234567890_abcdefghijklmnopqrstuvwxyz',
  9,
);

export const generateFastUnsafeLowerCaseShortid = /*#__PURE__*/ fastCustomAlphabet(
  '1234567890_abcdefghijklmnopqrstuvwxyz',
  9,
);

let counter = 0;
export const generateFastCountId = () => `fid_${Date.now()}_${counter++}`;

/**
 * 使用nanoid库, 生成数字加小写字母构成的UUID，不包含下划线且默认为 6 位
 */
export const generateLowerCaseShortidV2 = (size = 6) => {
  return customAlphabet('1234567890abcdefghijklmnopqrstuvwxyz', size)();
};

/**
 * 使用nanoid库, 生成数字构成的UUID
 */
export const generateNumberShortid = (size: number | undefined) =>
  Number(customAlphabet('123456789', 9)(size));
/**
 * 使用nanoid库, 生成数字, 小写字母, 大写字母,以及"-"分隔符构成的UUID
 */
export const uuidGeneral = () => {
  const uuid = uuidNanoid();
  return `${uuid.substring(0, 8)}-${uuid.substring(8, 12)}-${uuid.substring(
    12,
    16,
  )}-${uuid.substring(16, 20)}-${uuid.substring(20, 32)}`;
};
export const getUuidv4 = uuidGeneral;

/**
 * 将时间戳经过MD5哈希后转化成字符串UUID, 多对一映射关系, 与使用时间戳作为UUID没有本质区别
 * @param prefix  UUID前缀
 * @param length  生成的UUID长度, 最大不能超过md5哈希字符串的长度
 */
export function generateRandomId(prefix?: string, length?: number) {
  return `${prefix ? `${prefix}_` : ''}${MD5(
    new Date().getTime().toString(),
  ).slice(0, length || 6)}`;
}

let uuidIndex = 0;
/**
 * 将时间戳经过MD5哈希后, 在尾部追加一个自增计数器, 转化成字符串UUID
 */
export function createUuid() {
  return MD5(`${new Date().getTime()}_${uuidIndex++}`);
}

/**
 * 生成随机的UUID(生成的字符串包含数字, 小写字母, 大写字母, 分隔符为 - );
 * 1. 引入了时间戳, random随机数, 自增计数器三个变量, 减少重复的概率
 * @param prefix  uuid固定前缀
 */
export function createUUID(prefix = '') {
  return defaultIterator.create(prefix);
}

/**
 * UUID生成器, 可以使用它来自定义UUID的生成行为
 */
export class UuidGenerator {
  static createRandomStr() {
    return scaleTransition(Math.floor(Math.random() * 100000000));
  }

  protected readonly _cache = {
    // 分隔符
    splitChar: '.',
    // 基本字符
    baseChar: NUMBER_LOWER_UPPER,
    // uuid生成信息的map
    uuidInfo: {} as Record<
      string,
      { prefix: string; count: number } | undefined
    >,
  };

  get splitChar() {
    return this._cache.baseChar;
  }

  get baseChar() {
    return this._cache.baseChar;
  }

  constructor(splitChar?: string, baseChar?: string[]) {
    if (splitChar !== undefined) {
      this._cache.splitChar = splitChar;
    }
    if (baseChar) {
      this._cache.baseChar = baseChar;
    }
  }

  protected getUuidInfo(prefix: string): { prefix: string; count: number } {
    const { splitChar, uuidInfo } = this._cache;
    let uuidObject = uuidInfo[prefix];
    if (uuidObject === undefined) {
      if (prefix) {
        uuidObject = {
          prefix:
            prefix + splitChar + UuidGenerator.createRandomStr() + splitChar,
          count: 0,
        };
      } else {
        uuidObject = {
          prefix: UuidGenerator.createRandomStr() + splitChar,
          count: 0,
        };
      }
      uuidInfo[prefix] = uuidObject;
    }
    return uuidObject;
  }

  create(prefix = '') {
    const uuidObject = this.getUuidInfo(prefix);
    return (
      uuidObject.prefix + scaleTransition(uuidObject.count++, this.baseChar)
    );
  }
}
const defaultIterator = new UuidGenerator('-');
